import React, { useEffect, useState, useCallback } from "react";
import { api, ApiError } from "../lib/api.js";
import { EPLogo, IcLock } from "../components/shared.jsx";

// /admin — the admin console (§2). Not linked from any agent page. Its own session
// (ep_admin) is completely distinct from the agent session. On load we probe
// /admin/me: authed → console shell; otherwise → login.
export default function Admin() {
  const [authed, setAuthed] = useState(null); // null = checking, false = login, true = console

  useEffect(() => {
    api.get("/admin/me").then(() => setAuthed(true)).catch(() => setAuthed(false));
  }, []);

  if (authed === null) {
    return <Centered><p style={{ color: "var(--ep-fg-muted)" }}>Loading…</p></Centered>;
  }
  return authed ? <Console onSignOut={() => setAuthed(false)} /> : <Login onAuthed={() => setAuthed(true)} />;
}

function Centered({ children }) {
  return (
    <div style={{ minHeight: "100vh", display: "grid", placeItems: "center", background: "var(--ep-saguaro-blossom)", fontFamily: "var(--ep-font-body)" }}>
      {children}
    </div>
  );
}

function Login({ onAuthed }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.post("/admin/login", { email, password });
      // Sign-in succeeding does not mean this console will work: an
      // `agent_services` account has a valid password here but is refused by
      // requireAdmin on every route inside. Without this probe they land on a
      // console where nothing loads and no screen says why. Send them to /staff,
      // which is the thing they were looking for.
      await api.get("/admin/me");
      onAuthed();
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        setError("That account is Agent Services, not an admin. The portal for it is at /staff.");
      } else {
        setError(err instanceof ApiError && err.status === 503
          ? "Admin login isn't configured on the server yet."
          : "Invalid credentials.");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <Centered>
      <form onSubmit={submit} style={{ width: "min(400px, 92vw)", background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)", borderRadius: 4, padding: 32 }}>
        <div style={{ display: "flex", justifyContent: "center", marginBottom: 24 }}><EPLogo height={24} dark /></div>
        <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)", textAlign: "center" }}>Admin Console</div>
        <h1 style={{ font: "300 28px/1.1 var(--ep-font-display)", margin: "8px 0 24px", textAlign: "center" }}>Sign in</h1>

        <label className="ep-field">
          <span className="ep-eyebrow">Email</span>
          <input className="ep-input" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="username" autoFocus />
        </label>
        <label className="ep-field">
          <span className="ep-eyebrow">Password</span>
          <input className="ep-input" type="password" value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="current-password" />
        </label>

        {error && <div style={{ fontSize: 13, color: "var(--ep-rosewood)", margin: "4px 0 12px" }}>{error}</div>}

        <button type="submit" className="ep-btn primary" disabled={busy || !email || !password} style={{ width: "100%", marginTop: 8, justifyContent: "center" }}>
          {busy ? "Signing in…" : <><IcLock size={14} /> Sign in</>}
        </button>
      </form>
    </Centered>
  );
}

function Console({ onSignOut }) {
  async function signOut() {
    await api.post("/admin/logout").catch(() => {});
    onSignOut();
  }
  return (
    <div style={{ minHeight: "100vh", background: "var(--ep-saguaro-blossom)", fontFamily: "var(--ep-font-body)" }}>
      <header className="topbar">
        <div className="hstack" style={{ gap: 20 }}>
          <EPLogo height={20} />
          <span style={{ opacity: 0.3 }}>/</span>
          <span className="ep-caption" style={{ color: "rgba(255,245,228,0.75)" }}>Admin Console</span>
        </div>
        <div className="hstack" style={{ gap: 8 }}>
          {/* An admin session already satisfies /staff — requireStaff accepts both
              roles — so this is a plain link, not a second sign-in. It is here
              because otherwise the portal is only reachable by knowing the URL. A
              full page load rather than a router link: they are separate SPA
              entry points and the session cookie is shared across both. */}
          <a className="ep-btn ghost sm" href="/staff"
            style={{ color: "var(--ep-saguaro-blossom)", textDecoration: "none" }}>
            Agent Services portal
          </a>
          <button className="ep-btn ghost sm" onClick={signOut} style={{ color: "var(--ep-saguaro-blossom)" }}>Sign out</button>
        </div>
      </header>
      <div style={{ maxWidth: 1100, margin: "0 auto", padding: "40px clamp(24px, 4vw, 64px)" }}>
        <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Signed in</div>
        <h1 style={{ font: "300 40px/1.05 var(--ep-font-display)", margin: "8px 0 28px" }}>EveryPlace Admin</h1>
        <Pipeline />
        <div style={{ height: 40 }} />
        <MlsSettings />
        <div style={{ height: 40 }} />
        <ConnectionsSettings />
        <div style={{ height: 40 }} />
        <BrokerSignatures />
        <div style={{ height: 40 }} />
        <PendingBrokerSignatures />
        <div style={{ height: 40 }} />
        <AgentCheck />
        <div style={{ height: 40 }} />
        <TransferPanel />
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Pipeline — onboarding progress dashboard. Shows every agent from a given
// cohort date with their inferred status, doc counts, and per-envelope actions.
// ----------------------------------------------------------------------------
const STATUS_META = {
  complete:            { cls: "active",  label: "Complete"     },
  in_progress:         { cls: "signed",  label: "In Progress"  },
  agreements_pending:  { cls: "draft",   label: "Agreements Pending" },
  stuck:               { cls: "error",   label: "Stuck"        },
  error:               { cls: "error",   label: "Error"        },
  not_started:         { cls: "draft",   label: "Not Started"  },
};

const DOC_LABELS = {
  ica:                 "ICA",
  everyplace_package:  "EP Agreements",
  laughton_amendments: "Laughton Amendments",
  package:             "Agreement Package",
  listing_transfer:    "Listing Transfer",
  escrow_transfer:     "Escrow Transfer",
  member_change:       "Association Transfer",
  bba:                 "Buyer-Broker Agreement",
  bba_transfer:        "Buyer-Broker Transfer",
  w9:                  "W-9",
};

// Per-stage status vocabulary, shared by the row summary and the drilldown
// timeline. Mirrors the statuses the API emits in `stages` / `provisioning`.
const STAGE_META = {
  ok:      { cls: "active", dot: "var(--ep-avocado)",    label: "Done"    },
  waiting: { cls: "draft",  dot: "var(--ep-high-noon)",  label: "Waiting" },
  pending: { cls: "draft",  dot: "var(--ep-border-strong, #473C1666)", label: "Pending" },
  error:   { cls: "error",  dot: "var(--ep-sunset)",     label: "Error"   },
  skipped: { cls: "draft",  dot: "var(--ep-fg-subtle, #473C1699)",     label: "Skipped" },
  "n/a":   { cls: "draft",  dot: "transparent",          label: "N/A"     },
};

// Compact strip under the agent name: one dot per key stage, derived from the
// list endpoint's columns so opening the row isn't required to spot a problem.
function StageDots({ agent }) {
  const provStatus = agent.prov_errors > 0 ? "error"
    : agent.prov_total === 0 ? "pending"
    : agent.prov_ok === agent.prov_total ? "ok" : "waiting";

  const items = [
    ["Invite",  agent.welcome_email_sent_at ? "ok" : "pending"],
    ["ICA",     agent.ica_completed_at ? "ok" : "pending"],
    ["Form",    agent.welcome_form_done ? "ok" : "pending"],
    ["ADRE",    agent.adre_confirmed_at ? "ok" : "pending"],
    ["Assoc",   agent.association_submitted_at ? "ok" : "pending"],
    [agent.prov_total ? `Prov ${agent.prov_ok}/${agent.prov_total}` : "Prov", provStatus],
    ["Recap",   agent.recap_email_sent_at || agent.onboarding_completed_at ? "ok" : "pending"],
  ];

  return (
    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginTop: 6, alignItems: "center" }}>
      {items.map(([label, st]) => (
        <span key={label} title={`${label}: ${STAGE_META[st].label}`}
          style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10,
            color: st === "error" ? "var(--ep-sunset)" : "var(--ep-fg-muted)",
            textTransform: "uppercase", letterSpacing: "0.05em" }}>
          <span style={{ width: 7, height: 7, borderRadius: 999, background: STAGE_META[st].dot,
            border: st === "pending" ? "1px solid var(--ep-border-strong, #473C1666)" : "none",
            flexShrink: 0 }}/>
          {label}
        </span>
      ))}
      {agent.intake_error && (
        <span title={agent.intake_error}
          style={{ fontSize: 10, color: "var(--ep-sunset)", textTransform: "uppercase", letterSpacing: "0.05em" }}>
          · intake error
        </span>
      )}
    </div>
  );
}

// Full stage timeline for the drilldown. `stages` is the journey; `provisioning`
// is the 8-service step-2c ledger, nested so it reads as one stage with detail.
function StageTimeline({ stages = [], provisioning = [], notes, agentId, onRetried,
                        canSendRecap, agentEmail }) {
  // Which service is mid-retry, and the last error from one. Held here rather than
  // per-Row so a re-render from onRetried() cannot strand a spinner.
  const [retrying, setRetrying] = useState(null);
  const [retryErr, setRetryErr] = useState(null);
  const [sendingRecap, setSendingRecap] = useState(false);
  const [recapErr, setRecapErr] = useState(null);

  async function retry(service) {
    setRetrying(service); setRetryErr(null);
    try {
      await api.post(`/admin/pipeline/agents/${agentId}/provisioning/${service}/retry`, {});
      await onRetried?.();
    } catch (e) {
      setRetryErr(`${service}: ${e.message || "retry failed"}`);
    } finally { setRetrying(null); }
  }

  // Sends the agent's recap AND the staff summary — the server does both, from
  // the same code path the wizard uses. For an agent who never reached Finish
  // this is the only way they are told their mailbox password.
  async function sendRecap() {
    setSendingRecap(true); setRecapErr(null);
    try {
      await api.post(`/admin/pipeline/agents/${agentId}/send-recap`, {});
      await onRetried?.();
    } catch (e) {
      setRecapErr(e.message || "send failed");
    } finally { setSendingRecap(false); }
  }

  const Row = ({ s, indent, canRetry }) => {
    const meta = STAGE_META[s.status] || STAGE_META.pending;
    return (
      <div style={{ display: "grid",
        gridTemplateColumns: canRetry ? "18px 1fr 90px 1fr 64px" : "18px 1fr 90px 1fr", gap: 10,
        alignItems: "baseline", padding: "5px 0", paddingLeft: indent ? 22 : 0,
        borderBottom: "1px solid var(--ep-border)" }}>
        <span style={{ width: 9, height: 9, borderRadius: 999, background: meta.dot,
          border: s.status === "pending" ? "1px solid var(--ep-border-strong, #473C1666)" : "none",
          justifySelf: "center", marginTop: 4 }}/>
        <span style={{ fontSize: 13, color: indent ? "var(--ep-fg-muted)" : "var(--ep-fg)",
          fontWeight: indent ? 400 : 500 }}>{s.label}</span>
        <span className={`ep-badge ${meta.cls}`} style={{ fontSize: 10, justifySelf: "start" }}>
          {meta.label}
        </span>
        <span style={{ fontSize: 11, color: "var(--ep-fg-muted)", overflowWrap: "anywhere" }}>
          {s.at ? formatAge(s.at) : ""}{s.at && s.detail ? " · " : ""}{s.detail || ""}
        </span>
        {canRetry && (
          <button className="ep-btn ghost sm" style={{ fontSize: 10, justifySelf: "end" }}
            disabled={!!retrying}
            title={s.key === "brokermint_enrich"
              ? "Re-runs the BoldTrail user create. Brokermint has no delete endpoint, so confirm the user does not already exist."
              : `Re-run ${s.label}`}
            onClick={() => {
              // Only brokermint_enrich gets a confirm: it is the one service whose
              // partial success cannot be undone. The others are safely repeatable.
              if (s.key === "brokermint_enrich" &&
                  !window.confirm("Re-run the BoldTrail user create?\n\nBrokermint has no user-delete endpoint. If the earlier attempt partially succeeded the user may already exist — the server checks by email first, but confirm you expect this.")) return;
              retry(s.key);
            }}>
            {retrying === s.key ? "…" : "Retry"}
          </button>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginBottom: 18 }}>
      <div className="ep-eyebrow" style={{ fontSize: 10, color: "var(--ep-fg-muted)", marginBottom: 8 }}>
        Stage by stage
      </div>
      <div>
        {stages.map((s) => (
          <div key={s.key}>
            <Row s={s}/>
            {/* Provisioning detail sits under the welcome form, where it runs. */}
            {s.key === "welcome_form" && provisioning.length > 0 && (
              <>
                <div style={{ fontSize: 10, color: "var(--ep-fg-muted)", textTransform: "uppercase",
                  letterSpacing: "0.06em", padding: "8px 0 2px 22px" }}>
                  Provisioning (step 2c)
                </div>
                {provisioning.map((p) => (
                  // 'ok' deliberately has no button. The server rejects it with 409
                  // too — the invariant lives there, this just avoids offering it.
                  <Row key={p.key} s={p} indent canRetry={!!agentId && p.status !== "ok"}/>
                ))}
                {retryErr && (
                  <div style={{ fontSize: 11, color: "var(--ep-rosewood)", padding: "6px 0 0 22px" }}>
                    {retryErr}
                  </div>
                )}
              </>
            )}
            {/* Send-recap sits under the recap row, where it belongs. Shown only
                when the server says it is sendable (gate fired, not already
                sent) — the endpoint enforces both again, this just avoids
                offering a button that would 409. */}
            {s.key === "recap" && canSendRecap && agentId && (
              <div style={{ padding: "8px 0 0 22px" }}>
                <button className="ep-btn ghost sm" style={{ fontSize: 10 }}
                  disabled={sendingRecap}
                  title="Sends the agent their recap (including their mailbox password) and the staff summary"
                  onClick={() => {
                    if (!window.confirm(
                      `Send the onboarding recap to ${agentEmail || "this agent"}?\n\n`
                      + "This emails the agent their new Laughton Team address and mailbox "
                      + "password, and sends the onboarding summary to the seven-person staff "
                      + "roster. Both send once — there is no undo.")) return;
                    sendRecap();
                  }}>
                  {sendingRecap ? "Sending…" : "Send recap + staff summary"}
                </button>
                {recapErr && (
                  <div style={{ fontSize: 11, color: "var(--ep-rosewood)", paddingTop: 6 }}>
                    {recapErr}
                  </div>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {notes && (
        <div style={{ fontSize: 11, color: "var(--ep-fg-muted)", marginTop: 10, fontStyle: "italic" }}>
          Agent notes: {notes}
        </div>
      )}
    </div>
  );
}

function Pipeline() {
  const [date, setDate]               = useState("2026-06-16T10:00");
  const [showArchived, setShowArchived] = useState(false);
  // Off by default: the table is the RECRUIT pipeline. The agents table also
  // holds every transfer-day agent, who never ran this flow and whose stage
  // columns are therefore permanently blank — they buried the real rows.
  const [showAll, setShowAll] = useState(false);
  const [data, setData]               = useState(null);
  const [error, setError]             = useState(null);
  const [openAgent, setOpenAgent]     = useState(null); // agentId with drilldown open

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await api.get(`/admin/pipeline?date=${date}&archived=${showArchived}&all=${showAll}`);
      setData(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load pipeline.");
    }
  }, [date, showArchived, showAll]);

  useEffect(() => { load(); }, [load]);

  const { agents = [], stats = {} } = data || {};

  return (
    <section>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Onboarding Pipeline</div>
      <h2 style={{ font: "300 24px/1.15 var(--ep-font-display)", margin: "6px 0 4px" }}>Agent progress</h2>

      {/* Controls */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 20, marginTop: 12 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
          <span className="ep-eyebrow" style={{ fontSize: 11 }}>Show agents from</span>
          <input
            type="datetime-local"
            className="ep-input"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            style={{ padding: "4px 8px", fontSize: 13, width: "auto" }}
          />
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", color: "var(--ep-fg-muted)" }}>
          <input type="checkbox" checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} style={{ accentColor: "var(--ep-sunset)" }} />
          Show archived
        </label>
        <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, cursor: "pointer", color: "var(--ep-fg-muted)" }}
          title="Include transfer-day agents, who never went through this onboarding flow">
          <input type="checkbox" checked={showAll} onChange={(e) => setShowAll(e.target.checked)} style={{ accentColor: "var(--ep-sunset)" }} />
          Include transfer agents
        </label>
        <button className="ep-btn ghost sm" onClick={load} style={{ fontSize: 12 }}>Refresh</button>
      </div>

      {error && <div style={{ fontSize: 13, color: "var(--ep-rosewood)", marginBottom: 16 }}>{error}</div>}

      {/* Stats row */}
      {data && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 20 }}>
          {[
            { key: "total",             label: "Total",             cls: "" },
            { key: "complete",          label: "Complete",          cls: "active" },
            { key: "in_progress",       label: "In Progress",       cls: "signed" },
            { key: "agreements_pending",label: "Agreements Pending",cls: "draft" },
            { key: "stuck",             label: "Stuck",             cls: "error" },
            { key: "not_started",       label: "Not Started",       cls: "draft" },
          ].map(({ key, label, cls }) => (
            <div key={key} className="ep-card" style={{ padding: "10px 16px", textAlign: "center", minWidth: 80 }}>
              <div style={{ fontSize: 22, fontWeight: 300, fontFamily: "var(--ep-font-display)", lineHeight: 1 }}>
                {stats[key] ?? 0}
              </div>
              <div style={{ fontSize: 11, color: "var(--ep-fg-muted)", marginTop: 4, textTransform: "uppercase", letterSpacing: "0.06em" }}>{label}</div>
            </div>
          ))}
        </div>
      )}

      {/* Agent table */}
      {data === null ? (
        <p style={{ color: "var(--ep-fg-muted)" }}>Loading…</p>
      ) : agents.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--ep-fg-muted)" }}>No agents found for {date}.</p>
      ) : (
        <div style={{ display: "grid", gap: 2 }}>
          {/* Header */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 160px 80px 80px 120px 140px", gap: 8, padding: "6px 12px", fontSize: 11, fontWeight: 600, color: "var(--ep-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            <span>Agent</span><span>Status</span><span>Sent</span><span>Done</span><span>Last Activity</span><span></span>
          </div>
          {agents.map((agent) => (
            <PipelineRow
              key={agent.id}
              agent={agent}
              open={openAgent === agent.id}
              onToggle={() => setOpenAgent(openAgent === agent.id ? null : agent.id)}
              onChanged={load}
            />
          ))}
        </div>
      )}
    </section>
  );
}

function PipelineRow({ agent, open, onToggle, onChanged }) {
  const sm = STATUS_META[agent.status] || { cls: "draft", label: agent.status };
  const lastActivity = agent.last_activity
    ? formatAge(agent.last_activity)
    : "—";

  return (
    <div className="ep-card" style={{ padding: 0, overflow: "hidden" }}>
      <div
        style={{ display: "grid", gridTemplateColumns: "1fr 160px 80px 80px 120px 140px", gap: 8, padding: "12px 16px", alignItems: "center", cursor: "pointer" }}
        onClick={onToggle}
      >
        <div>
          <div style={{ fontWeight: 500, fontSize: 14 }}>{agent.name || agent.email}</div>
          <div style={{ fontSize: 11, color: "var(--ep-fg-muted)", marginTop: 2 }}>
            {agent.email}{agent.mls_short_id ? ` · ${agent.mls_short_id}` : ""}
            {agent.archived_at && <span style={{ color: "var(--ep-rosewood)", marginLeft: 8 }}>archived</span>}
          </div>
          <StageDots agent={agent}/>
        </div>
        <span className={`ep-badge ${sm.cls}`}>{sm.label}</span>
        <span style={{ fontSize: 13 }}>{agent.docs_sent}</span>
        <span style={{ fontSize: 13 }}>{agent.docs_done}</span>
        <span style={{ fontSize: 12, color: "var(--ep-fg-muted)" }}>{lastActivity}</span>
        <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
          <button className="ep-btn sm" onClick={(e) => { e.stopPropagation(); onToggle(); }} style={{ fontSize: 12 }}>
            {open ? "Close" : "View"}
          </button>
        </div>
      </div>
      {open && <AgentDrilldown agentId={agent.id} archived={!!agent.archived_at} onChanged={onChanged} />}
    </div>
  );
}

function AgentDrilldown({ agentId, archived, onChanged }) {
  const [data, setData]     = useState(null);
  const [error, setError]   = useState(null);
  const [busy, setBusy]     = useState({});

  const load = useCallback(async () => {
    setError(null);
    try {
      const d = await api.get(`/admin/pipeline/${agentId}`);
      setData(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load agent details.");
    }
  }, [agentId]);

  useEffect(() => { load(); }, [load]);

  async function remind(envelopeId) {
    setBusy((b) => ({ ...b, [envelopeId + "_remind"]: true }));
    try {
      await api.post(`/admin/pipeline/envelopes/${encodeURIComponent(envelopeId)}/remind`, {});
      load();
    } catch (err) {
      alert("Remind failed: " + (err instanceof ApiError ? err.message : err.message));
    } finally {
      setBusy((b) => ({ ...b, [envelopeId + "_remind"]: false }));
    }
  }

  async function cancel(envelopeId, subject) {
    if (!window.confirm(`Cancel "${subject}"? This cannot be undone.`)) return;
    setBusy((b) => ({ ...b, [envelopeId + "_cancel"]: true }));
    try {
      await api.del(`/admin/pipeline/envelopes/${encodeURIComponent(envelopeId)}`);
      load();
    } catch (err) {
      alert("Cancel failed: " + (err instanceof ApiError ? err.message : err.message));
    } finally {
      setBusy((b) => ({ ...b, [envelopeId + "_cancel"]: false }));
    }
  }

  async function toggleArchive() {
    const label = archived ? "Unarchive" : "Archive";
    if (!window.confirm(`${label} this agent? ${archived ? "They will reappear in the pipeline." : "They will be hidden from the pipeline."}`)) return;
    try {
      if (archived) {
        await api.del(`/admin/pipeline/agents/${agentId}/archive`);
      } else {
        await api.post(`/admin/pipeline/agents/${agentId}/archive`, {});
      }
      onChanged();
    } catch (err) {
      alert(`${label} failed: ` + (err instanceof ApiError ? err.message : err.message));
    }
  }

  return (
    <div style={{ borderTop: "1px solid var(--ep-border)", background: "var(--ep-bg-page, #f5f0e8)", padding: "16px 20px" }}>
      {error && <div style={{ fontSize: 13, color: "var(--ep-rosewood)", marginBottom: 12 }}>{error}</div>}
      {data === null && !error && <p style={{ fontSize: 13, color: "var(--ep-fg-muted)" }}>Loading…</p>}

      {data && (
        <>
          {/* Stage-by-stage timeline — the whole journey, not just envelopes */}
          <StageTimeline stages={data.stages} provisioning={data.provisioning} notes={data.notes}
            agentId={agentId} onRetried={load} canSendRecap={data.canSendRecap}
            agentEmail={data.agent?.email}/>

          {/* Document list */}
          <div className="ep-eyebrow" style={{ fontSize: 10, color: "var(--ep-fg-muted)", marginBottom: 8 }}>
            Envelopes
          </div>
          {data.documents.length === 0 ? (
            <p style={{ fontSize: 13, color: "var(--ep-fg-muted)", marginBottom: 16 }}>No documents sent yet.</p>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginBottom: 16 }}>
              <thead>
                <tr style={{ borderBottom: "1px solid var(--ep-border)" }}>
                  <Th>Type</Th><Th>Subject</Th><Th>Status</Th><Th>Age</Th><Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {data.documents.map((doc) => {
                  const docSm = doc.status === "completed" || doc.status === "delivered"
                    ? { cls: "active", label: "Signed" }
                    : doc.status === "revoked"
                    ? { cls: "draft", label: "Revoked" }
                    : doc.status === "error"
                    ? { cls: "error", label: "Error" }
                    : doc.stuck
                    ? { cls: "error", label: "Stuck" }
                    : { cls: "draft", label: "Pending" };
                  const canAct = doc.status === "sent";
                  return (
                    // envelope_id, not doc.id: the detail endpoint UNIONs
                    // signed_documents with escrow_transfer_envelopes and never
                    // selects id, so doc.id is undefined for every row.
                    <tr key={doc.envelope_id} style={{ borderBottom: "1px solid var(--ep-border)" }}>
                      <Td><span style={{ fontSize: 11, color: "var(--ep-fg-muted)" }}>{DOC_LABELS[doc.doc_type] || doc.doc_type}</span></Td>
                      <Td>{doc.subject || "—"}</Td>
                      <Td><span className={`ep-badge ${docSm.cls}`} style={{ fontSize: 11 }}>{docSm.label}</span></Td>
                      <Td style={{ color: "var(--ep-fg-muted)" }}>{doc.hours_age != null ? `${doc.hours_age}h` : "—"}</Td>
                      <Td>
                        {canAct && (
                          <div style={{ display: "flex", gap: 6 }}>
                            <button
                              className="ep-btn sm"
                              style={{ fontSize: 11 }}
                              disabled={busy[doc.envelope_id + "_remind"]}
                              onClick={() => remind(doc.envelope_id)}
                            >
                              {busy[doc.envelope_id + "_remind"] ? "…" : "Remind"}
                            </button>
                            <button
                              className="ep-btn ghost sm"
                              style={{ fontSize: 11, color: "var(--ep-rosewood)" }}
                              disabled={busy[doc.envelope_id + "_cancel"]}
                              onClick={() => cancel(doc.envelope_id, doc.subject)}
                            >
                              {busy[doc.envelope_id + "_cancel"] ? "…" : "Cancel"}
                            </button>
                          </div>
                        )}
                      </Td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}

          {/* Archive / unarchive */}
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <button
              className="ep-btn ghost sm"
              style={{ fontSize: 12, color: archived ? "var(--ep-saguaro)" : "var(--ep-rosewood)" }}
              onClick={toggleArchive}
            >
              {archived ? "Unarchive agent" : "Archive agent"}
            </button>
            <span style={{ fontSize: 11, color: "var(--ep-fg-muted)" }}>
              {archived ? "Reappears in the pipeline." : "Hides from pipeline view."}
            </span>
          </div>
        </>
      )}
    </div>
  );
}

function formatAge(ts) {
  const diffMs = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diffMs / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// ----------------------------------------------------------------------------
// Connections — API-key integrations (BoldTrail BackOffice, Follow Up Boss).
// Paste a key → encrypted at rest → Test. Same pattern as MLS Settings.
// ----------------------------------------------------------------------------
function ConnectionsSettings() {
  const [conns, setConns] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await api.get("/admin/connections");
      setConns(data.connections || {});
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load connections.");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <section>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Connections</div>
      <h2 style={{ font: "300 24px/1.15 var(--ep-font-display)", margin: "6px 0 4px" }}>API integrations</h2>
      <p style={{ fontSize: 13, color: "var(--ep-fg-muted)", maxWidth: "62ch", marginBottom: 20 }}>
        Paste each service's API key, then Save and Test. Keys are encrypted at rest and never shown again.
      </p>
      {error && <div style={{ fontSize: 13, color: "var(--ep-rosewood)", marginBottom: 16 }}>{error}</div>}
      {conns === null ? (
        <p style={{ color: "var(--ep-fg-muted)" }}>Loading…</p>
      ) : (
        <div style={{ display: "grid", gap: 16, maxWidth: 760 }}>
          {Object.entries(conns).map(([key, c]) => (
            <ConnectionCard key={key} service={key} conn={c} onChanged={load} />
          ))}
        </div>
      )}
    </section>
  );
}

function ConnectionCard({ service, conn, onChanged }) {
  const [apiKey, setApiKey] = useState(""); // write-only; never pre-filled
  const [busy, setBusy] = useState(null);   // 'save' | 'test' | 'disconnect'
  const [msg, setMsg] = useState(null);

  const status = conn.connected ? { cls: "active", label: "Connected" }
    : conn.status === "error" ? { cls: "error", label: conn.lastError || "Error" }
    : { cls: "draft", label: "Not connected" };

  function fail(err, fallback) { setMsg({ ok: false, text: err instanceof ApiError ? err.message : fallback }); }

  async function save(e) {
    e.preventDefault();
    setBusy("save"); setMsg(null);
    try {
      await api.patch(`/admin/connections/${service}`, { apiKey: apiKey.trim() });
      setApiKey("");
      setMsg({ ok: true, text: "Saved." });
      onChanged();
    } catch (err) { fail(err, "Failed to save."); }
    finally { setBusy(null); }
  }
  async function test() {
    setBusy("test"); setMsg(null);
    try {
      const out = await api.post(`/admin/connections/${service}/test`, {});
      setMsg(out.ok
        ? { ok: true, text: `Connected ✓ (${out.durationMs}ms)` }
        : { ok: false, text: `Test failed: ${out.message || out.code || out.status || "unknown"}` });
      onChanged();
    } catch (err) { fail(err, "Test failed."); }
    finally { setBusy(null); }
  }
  async function disconnect() {
    if (!window.confirm(`Disconnect ${conn.label}? Its API key is cleared.`)) return;
    setBusy("disconnect"); setMsg(null);
    try { await api.del(`/admin/connections/${service}`); setMsg({ ok: true, text: "Disconnected." }); onChanged(); }
    catch (err) { fail(err, "Disconnect failed."); }
    finally { setBusy(null); }
  }

  return (
    <div className="ep-card" style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div style={{ font: "300 22px/1.15 var(--ep-font-display)" }}>{conn.label}</div>
          <div style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginTop: 4 }}>
            API key: <strong>{conn.hasKey ? "set" : "not set"}</strong>
          </div>
        </div>
        <span className={`ep-badge ${status.cls}`}>{status.label}</span>
      </div>

      <form onSubmit={save} style={{ marginTop: 16, display: "grid", gap: 10 }}>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">API Key</span>
          <input className="ep-input" type="password" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
            autoComplete="new-password" placeholder={conn.hasKey ? "•••• (set — paste to replace)" : "Paste the API key"} />
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button type="submit" className="ep-btn primary sm" disabled={busy === "save" || !apiKey.trim()}>
            {busy === "save" ? "Saving…" : "Save"}
          </button>
          <button type="button" className="ep-btn sm" onClick={test} disabled={busy === "test" || !conn.hasKey}>
            {busy === "test" ? "Testing…" : "Test"}
          </button>
          {conn.hasKey && (
            <button type="button" className="ep-btn ghost sm" onClick={disconnect} disabled={busy === "disconnect"} style={{ color: "var(--ep-rosewood)" }}>
              {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
            </button>
          )}
        </div>
      </form>
      {msg && <div style={{ fontSize: 12, marginTop: 12, color: msg.ok ? "var(--ep-saguaro, #2f6b43)" : "var(--ep-rosewood)" }}>{msg.text}</div>}
    </div>
  );
}

// ----------------------------------------------------------------------------
// MLS Settings — one card per Spark feed. Paste the OAuth Key + Access Token,
// Save (active immediately), Test, Disconnect. Talks to /admin/mls/feeds.
// ----------------------------------------------------------------------------
function MlsSettings() {
  const [feeds, setFeeds] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const data = await api.get("/admin/mls/feeds");
      setFeeds(data.feeds || []);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to load feeds.");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <section>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>MLS Settings</div>
      <h2 style={{ font: "300 24px/1.15 var(--ep-font-display)", margin: "6px 0 4px" }}>Spark feed connections</h2>
      <p style={{ fontSize: 13, color: "var(--ep-fg-muted)", maxWidth: "62ch", marginBottom: 20 }}>
        Paste each feed's <strong>OAuth Key</strong> and <strong>Access Token</strong> (issued by the MLS), then Save.
        Once a feed shows <strong>Connected</strong>, agent listings and member enrichment light up with live data.
        The Access Token is a full-feed credential — it's encrypted at rest and never shown again.
      </p>

      {error && <div style={{ fontSize: 13, color: "var(--ep-rosewood)", marginBottom: 16 }}>{error}</div>}

      {feeds === null ? (
        <p style={{ color: "var(--ep-fg-muted)" }}>Loading feeds…</p>
      ) : feeds.length === 0 ? (
        <p style={{ color: "var(--ep-fg-muted)" }}>No feeds provisioned.</p>
      ) : (
        <div style={{ display: "grid", gap: 16, maxWidth: 760 }}>
          {feeds.map((f) => <FeedCard key={f.code} feed={f} onChanged={load} />)}
        </div>
      )}
    </section>
  );
}

// ----------------------------------------------------------------------------
// Broker Signatures — Amber Valentine (Designated Broker) and Michelle Bleach
// (Team Lead Director) each adopt their signature once here. The stored mark
// auto-applies to every BoldSign envelope where their email is a recipient.
// ----------------------------------------------------------------------------
// Signature pad geometry.
//
// SIG_PAD_W/H are the pad's LOGICAL size — the reference the 2px pen weight was
// chosen against, and what the aspect ratio is drawn at. SIG_PAD_SCALE raises the
// backing store above that, so a drawn mark is captured at higher resolution than
// it is displayed: BoldSign scales the exported PNG to the template's signature
// field, and unlike the typed path a drawn stroke cannot be re-rendered later, so
// whatever detail is captured here is all there will ever be.
//
// Safe to change either number. canvasPoint() converts pointer positions by the
// backingStore/displayed ratio and the pen weight is derived from c.width, so no
// geometry is pinned to these values.
const SIG_PAD_W = 380;
const SIG_PAD_H = 100;
const SIG_PAD_SCALE = 3;

// The signer list comes from GET /v1/broker-signature/roster, which derives it
// from the ROLES the send path actually queries. It used to be hardcoded here,
// which could silently disagree with the backend: the ICA looked for a mark at
// ROLES.AGENT_SERVICES.email while this list offered a different address, so a
// role could read "adopted" here and still fail to sign. Never hardcode it again.
function BrokerSignatures() {
  const [roster, setRoster] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    api.get("/v1/broker-signature/roster")
      .then(setRoster)
      .catch(e => setErr(e?.message || "Could not load the signer roster"));
  }, []);

  return (
    <section>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Broker Signatures</div>
      <h2 style={{ font: "300 24px/1.15 var(--ep-font-display)", margin: "6px 0 4px" }}>Signature adoption</h2>
      <p style={{ fontSize: 13, color: "var(--ep-fg-muted)", maxWidth: "62ch", marginBottom: 20 }}>
        Each signer adopts their mark once. It auto-applies to every BoldSign envelope where their email appears as a counter-signer, eliminating manual signing on every document. These are the exact addresses the send path looks up.
      </p>

      {err && <div style={{ fontSize: 13, color: "var(--ep-danger, #b3261e)", marginBottom: 16 }}>{err}</div>}
      {!roster && !err && <div style={{ fontSize: 13, color: "var(--ep-fg-muted)" }}>Loading signers…</div>}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 20 }}>
        {roster?.signers.map(s => <SignerPanel key={s.key} signer={s} />)}
      </div>

      {/* A mark nobody looks up is precisely the state worth surfacing — it reads
          as "adopted" to whoever adopted it while signing nothing. */}
      {roster?.orphans?.length > 0 && (
        <div style={{ marginTop: 24, padding: "12px 14px", border: "1px solid var(--ep-border)", borderRadius: 2, maxWidth: "62ch" }}>
          <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: ".04em", textTransform: "uppercase", color: "var(--ep-fg-muted)", marginBottom: 6 }}>
            Adopted but unused
          </div>
          <p style={{ fontSize: 13, color: "var(--ep-fg-muted)", margin: "0 0 8px" }}>
            These marks are on file but no signing role points at their address, so they are never applied to a document.
          </p>
          {roster.orphans.map(o => (
            <div key={o.email} style={{ fontSize: 13 }}>
              {o.name} — <span style={{ color: "var(--ep-fg-muted)" }}>{o.email}</span>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

function SignerPanel({ signer }) {
  const [status, setStatus] = useState(null);  // null | { adopted, name, email, adoptedAt }
  const [mode, setMode]     = useState("type"); // "type" | "draw"
  const [typedName, setTypedName] = useState(signer.name);
  const [consent, setConsent] = useState(false);
  const [busy, setBusy]     = useState(false);
  const [err, setErr]       = useState(null);
  const canvasRef           = React.useRef(null);
  const [drawing, setDrawing] = useState(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  // Load current status.
  useEffect(() => {
    api.get(`/v1/broker-signature?email=${encodeURIComponent(signer.email)}`)
      .then(d => setStatus(d))
      .catch(() => setStatus({ adopted: false }));
  }, [signer.email]);

  // Canvas drawing helpers.
  //
  // A pointer position has to be converted from CSS pixels into the canvas's own
  // coordinate space, and those are NOT the same thing. The element declares
  // width/height attributes (its backing store) but is styled `width: 100%`, so
  // the browser stretches it to the container. Using the bounding-rect offset
  // directly — which both handlers used to do — means every stroke lands
  // displaced by the ratio between the two, horizontally stretched or squashed
  // relative to the cursor. It only looked right at exactly one container width.
  //
  // Scaling by backingStore/displayed also makes the backing store's size
  // independent of the layout, which is what lets it be raised for resolution
  // without touching anything here.
  function canvasPoint(c, e) {
    const r = c.getBoundingClientRect();
    const src = e.touches?.[0] ?? e;
    return {
      x: (src.clientX - r.left) * (c.width / r.width),
      y: (src.clientY - r.top) * (c.height / r.height),
    };
  }

  function startDraw(e) {
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const { x, y } = canvasPoint(c, e);
    ctx.beginPath();
    ctx.moveTo(x, y);
    setDrawing(true);
  }
  function draw(e) {
    if (!drawing) return;
    const c = canvasRef.current; if (!c) return;
    const ctx = c.getContext("2d");
    const { x, y } = canvasPoint(c, e);
    ctx.lineTo(x, y);
    ctx.strokeStyle = "#473C16";
    // Stroke weight in CANVAS units, so raising the backing-store resolution
    // does not turn the pen into a hairline. SIG_PAD_W is the reference width the
    // 2px weight was chosen against.
    ctx.lineWidth = 2 * (c.width / SIG_PAD_W);
    // Handwriting needs round caps and joins; the default butt/miter gives
    // visibly angular corners on a signature.
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.stroke();
    setHasDrawn(true);
  }
  function stopDraw() { setDrawing(false); }
  function clearCanvas() {
    const c = canvasRef.current; if (!c) return;
    c.getContext("2d").clearRect(0, 0, c.width, c.height);
    setHasDrawn(false);
  }

  async function adopt() {
    if (!consent) return;
    setBusy(true); setErr(null);
    try {
      // BoldSign scales this PNG to whatever the template's signature field
      // measures, so the render resolution decides how it looks on the contract.
      // 300x85 was the old output; any field larger than that upscaled, and
      // upscaling a signature looks exactly as bad as it sounds — PDFs are also
      // viewed and printed well above 72 DPI, so even a same-size field could
      // render soft. Rendering larger means BoldSign DOWNSCALES, which is the
      // direction that stays clean. Costs a few KB of base64.
      const LOGICAL_W = 300, LOGICAL_H = 85, SCALE = 3;

      let signaturePng;
      if (mode === "draw") {
        const c = canvasRef.current;
        if (!c || !hasDrawn) { setErr("Please draw your signature first."); setBusy(false); return; }
        // Export at the drawing canvas's own resolution (380x100) rather than
        // squeezing it into 300x85, which discarded detail for no reason. A drawn
        // mark cannot gain detail it never had, so there is nothing to be won by
        // scaling it up beyond its source.
        const out = document.createElement("canvas");
        out.width = c.width; out.height = c.height;
        out.getContext("2d").drawImage(c, 0, 0, c.width, c.height);
        signaturePng = out.toDataURL("image/png");
      } else {
        // Rasterize the typed name to the PNG that BoldSign prefills onto every
        // ICA. This is the ONLY place the adopted signature's appearance is
        // decided — the previews elsewhere are cosmetic.
        //
        // CANVAS DOES NOT WAIT FOR WEBFONTS. If 'Alex Brush' has not finished
        // loading when fillText runs, canvas silently substitutes a generic
        // serif: no error, no warning, and a wrong signature baked into a
        // contract. So load it explicitly, then refuse rather than guess.
        const SIZE = 44;   // Alex Brush has a small x-height; 44 reads like 34 in Georgia
        const family = "'Alex Brush', cursive";
        try {
          await document.fonts.load(`${SIZE}px ${family}`, typedName);
        } catch { /* check below decides */ }
        if (!document.fonts.check(`${SIZE}px 'Alex Brush'`)) {
          setErr("The signature font hasn't finished loading — wait a moment and adopt again.");
          setBusy(false);
          return;
        }

        const c = document.createElement("canvas");
        c.width = LOGICAL_W * SCALE; c.height = LOGICAL_H * SCALE;
        const ctx = c.getContext("2d");
        // Draw in LOGICAL units and let the transform do the scaling, so every
        // measurement below — the fit loop, the 14px inset, the baseline — stays
        // in the same coordinate space regardless of SCALE. measureText respects
        // the transform's font size, not the device pixels.
        ctx.scale(SCALE, SCALE);
        ctx.fillStyle = "#473C16";

        // Shrink to fit rather than overflow the canvas. Names vary a lot
        // ("Kathie Luz" vs "Julia O'Buckley"), and a clipped signature is worse
        // than a slightly smaller one.
        let size = SIZE;
        ctx.font = `${size}px ${family}`;
        while (size > 22 && ctx.measureText(typedName).width > LOGICAL_W - 28) {
          size -= 2;
          ctx.font = `${size}px ${family}`;
        }
        // Baseline scales with the size so a shrunken mark stays vertically centred.
        ctx.fillText(typedName, 14, 52 + size * 0.14);
        signaturePng = c.toDataURL("image/png");
      }
      await api.put("/v1/broker-signature", {
        email: signer.email, name: signer.name, title: signer.title,
        signaturePng,
        consentText: "ESIGN/UETA electronic-records and electronic-signature consent accepted via EveryPlace Admin.",
      });
      const updated = await api.get(`/v1/broker-signature?email=${encodeURIComponent(signer.email)}`);
      setStatus(updated);
      setConsent(false);
    } catch (e) { setErr(e.message || "Could not save signature"); }
    finally { setBusy(false); }
  }

  async function revoke() {
    if (!window.confirm(`Revoke ${signer.name}'s signature? Future envelopes will require manual signing.`)) return;
    setBusy(true);
    try {
      await api.del(`/v1/broker-signature?email=${encodeURIComponent(signer.email)}`);
      setStatus({ adopted: false });
    } catch (e) { setErr(e.message); }
    finally { setBusy(false); }
  }

  if (!status) return <div className="ep-card" style={{ padding: 24 }}>Loading…</div>;

  return (
    <div className="ep-card" style={{ padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
        <div>
          <div style={{ font: "600 15px/1.3 var(--ep-font-body)" }}>{signer.name}</div>
          <div style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginTop: 2 }}>{signer.title}</div>
          <div style={{ fontSize: 11, color: "var(--ep-fg-muted)" }}>{signer.email}</div>
        </div>
        {status.adopted && (
          <span className="ep-badge signed" style={{ fontSize: 11 }}><span>✓</span> Adopted</span>
        )}
      </div>

      {/* What this mark is FOR, so a missing one is actionable rather than a
          mystery. Two of the three fail silently on the send path — the envelope
          ships with blank signature fields — so an un-adopted role that says
          silentIfMissing is called out explicitly here. */}
      {signer.usedFor && (
        <div style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginTop: -6 }}>
          {signer.usedFor}
          {!status.adopted && signer.silentIfMissing && (
            <div style={{ color: "var(--ep-rosewood)", marginTop: 4 }}>
              Not adopted — these documents send with this signature blank, without erroring.
            </div>
          )}
        </div>
      )}

      {/* The adopted name can differ from the role's configured name — worth
          seeing, since the role name is what gets stamped into the document. */}
      {status.adopted && signer.adoptedAs && signer.adoptedAs !== signer.name && (
        <div style={{ fontSize: 12, color: "var(--ep-rosewood)", marginTop: -6 }}>
          Mark was adopted as “{signer.adoptedAs}”, but documents are stamped “{signer.name}”.
        </div>
      )}

      {status.adopted ? (
        <>
          <div style={{ fontSize: 13, color: "var(--ep-fg-muted)" }}>
            Adopted {status.adoptedAt ? new Date(status.adoptedAt).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}. Auto-applies to all matching envelopes.
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button className="ep-btn secondary sm" onClick={() => setStatus({ adopted: false })}>Re-adopt</button>
            <button className="ep-btn ghost sm" onClick={revoke} disabled={busy} style={{ color: "var(--ep-rosewood)" }}>Revoke</button>
          </div>
        </>
      ) : (
        <>
          {/* Mode toggle */}
          <div style={{ display: "flex", gap: 8 }}>
            {["type", "draw"].map(m => (
              <button key={m} className={`ep-btn ${mode === m ? "primary" : "ghost"} sm`} onClick={() => setMode(m)}>
                {m === "type" ? "Type" : "Draw"}
              </button>
            ))}
          </div>

          {mode === "type" ? (
            // Must match the rasterizer in adopt(). This is the ONLY signature
            // surface the adopter actually looks at, and it was left on Georgia
            // italic when the rest moved to Alex Brush — so the box showed a serif
            // while the PNG came out as a script. No `fontStyle: italic`: Alex
            // Brush is already a slanted script, and italic on a font with no
            // italic face makes the browser synthesise a second slant.
            <input className="ep-input" value={typedName}
              onChange={e => setTypedName(e.target.value)}
              style={{ fontFamily: "'Alex Brush', cursive", fontSize: 30, lineHeight: 1.6,
                       color: "var(--ep-origin)" }}/>
          ) : (
            <div style={{ border: "1px solid var(--ep-border)", borderRadius: 2, background: "#FFFAF0", position: "relative" }}>
              <canvas ref={canvasRef}
                width={SIG_PAD_W * SIG_PAD_SCALE} height={SIG_PAD_H * SIG_PAD_SCALE}
                style={{ display: "block", width: "100%", cursor: "crosshair" }}
                onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw}/>
              {!hasDrawn && (
                <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)",
                  color: "var(--ep-fg-muted)", fontSize: 13, pointerEvents: "none", opacity: 0.5 }}>
                  Sign here
                </div>
              )}
              <button className="ep-btn ghost sm" onClick={clearCanvas}
                style={{ position: "absolute", top: 6, right: 6, fontSize: 11 }}>Clear</button>
            </div>
          )}

          <label style={{ display: "flex", alignItems: "flex-start", gap: 8, cursor: "pointer", fontSize: 12 }}>
            <input type="checkbox" checked={consent} onChange={e => setConsent(e.target.checked)}
              style={{ marginTop: 2, accentColor: "var(--ep-sunset)" }}/>
            <span style={{ color: "var(--ep-fg-muted)", lineHeight: 1.5 }}>
              I, <strong>{signer.name}</strong>, consent to use this electronic mark as my legally binding signature under ESIGN/UETA on all EveryPlace documents where I am a counter-signer.
            </span>
          </label>

          {err && <div style={{ fontSize: 12, color: "var(--ep-rosewood)" }}>{err}</div>}

          <button className="ep-btn primary sm" onClick={adopt} disabled={!consent || busy}>
            {busy ? "Saving…" : "Adopt Signature"}
          </button>
        </>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Pending Broker Signatures — everyplace_package envelopes in 'sent' state.
// Admin clicks Sign to generate a fresh 1-click embedded URL for Amber.
// ----------------------------------------------------------------------------
function PendingBrokerSignatures() {
  const [rows, setRows] = useState(null);
  const [signing, setSigning] = useState({});

  const load = useCallback(async () => {
    try {
      const data = await api.get("/v1/broker-signature/pending");
      setRows(data.pending || []);
    } catch { setRows([]); }
  }, []);
  useEffect(() => { load(); }, [load]);

  async function sign(envelopeId) {
    setSigning(s => ({ ...s, [envelopeId]: "loading" }));
    try {
      const { signUrl } = await api.get(`/v1/broker-signature/sign-url?envelopeId=${encodeURIComponent(envelopeId)}`);
      window.open(signUrl, "_blank", "noopener");
      setSigning(s => ({ ...s, [envelopeId]: null }));
    } catch {
      setSigning(s => ({ ...s, [envelopeId]: "error" }));
    }
  }

  return (
    <section>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Broker Counter-Signing</div>
      <h2 style={{ font: "300 24px/1.15 var(--ep-font-display)", margin: "6px 0 4px" }}>Pending broker signatures</h2>
      <p style={{ fontSize: 13, color: "var(--ep-fg-muted)", maxWidth: "62ch", marginBottom: 20 }}>
        EveryPlace agreements sent to agents that Amber has not yet counter-signed. Click <strong>Sign</strong> to open her 1-click embedded signing link.
      </p>
      {rows === null ? (
        <p style={{ color: "var(--ep-fg-muted)" }}>Loading…</p>
      ) : rows.length === 0 ? (
        <p style={{ fontSize: 13, color: "var(--ep-fg-muted)" }}>No pending signatures.</p>
      ) : (
        <div style={{ display: "grid", gap: 8, maxWidth: 760 }}>
          {rows.map(row => (
            <div key={row.envelope_id} className="ep-card"
              style={{ padding: "14px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: 14 }}>{row.notify_name || "Unknown agent"}</div>
                <div style={{ fontSize: 11, color: "var(--ep-fg-muted)", fontFamily: "Consolas, monospace", marginTop: 3 }}>
                  {new Date(row.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  {" · "}{row.envelope_id}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {signing[row.envelope_id] === "error" && (
                  <span style={{ fontSize: 12, color: "var(--ep-rosewood)" }}>Failed — may not be ready</span>
                )}
                <button className="ep-btn primary sm"
                  disabled={signing[row.envelope_id] === "loading"}
                  onClick={() => sign(row.envelope_id)}>
                  {signing[row.envelope_id] === "loading" ? "Opening…" : "Sign"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
      <button className="ep-btn ghost sm" onClick={load} style={{ marginTop: 12, fontSize: 12 }}>Refresh</button>
    </section>
  );
}

// ----------------------------------------------------------------------------
// Agent Check — type an MLS short id, see listings / pendings / BBAs instantly.
// ----------------------------------------------------------------------------
function AgentCheck() {
  const [mlsId, setMlsId] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function check(e) {
    e.preventDefault();
    const q = mlsId.trim().toLowerCase();
    if (!q) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const data = await api.get(`/admin/agent-check?mlsId=${encodeURIComponent(q)}`);
      setResult(data);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Check failed.");
    } finally { setBusy(false); }
  }

  const fmtPrice = (p) => p ? `$${Number(p).toLocaleString()}` : null;

  return (
    <section>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Diagnostics</div>
      <h2 style={{ font: "300 24px/1.15 var(--ep-font-display)", margin: "6px 0 4px" }}>Agent check</h2>
      <p style={{ fontSize: 13, color: "var(--ep-fg-muted)", maxWidth: "62ch", marginBottom: 20 }}>
        Enter an agent's short MLS ID to see their current listings, pendings, and BBAs.
      </p>

      <form onSubmit={check} style={{ display: "flex", gap: 8, maxWidth: 400, marginBottom: 24 }}>
        <input
          className="ep-input"
          value={mlsId}
          onChange={(e) => setMlsId(e.target.value)}
          placeholder="e.g. gl139"
          autoComplete="off"
          style={{ flex: 1 }}
        />
        <button type="submit" className="ep-btn primary sm" disabled={busy || !mlsId.trim()}>
          {busy ? "Checking…" : "Check"}
        </button>
      </form>

      {error && <div style={{ fontSize: 13, color: "var(--ep-rosewood)", marginBottom: 16 }}>{error}</div>}

      {result && (
        <div style={{ maxWidth: 760, display: "grid", gap: 16 }}>
          {/* Agent identity */}
          <div className="ep-card" style={{ padding: 20 }}>
            <div className="ep-eyebrow" style={{ marginBottom: 8 }}>Agent</div>
            {result.agent ? (
              <div style={{ fontSize: 14 }}>
                <strong>{result.agent.name}</strong>
                <span style={{ color: "var(--ep-fg-muted)", marginLeft: 12 }}>{result.agent.email}</span>
                <span style={{ color: "var(--ep-fg-muted)", marginLeft: 12 }}>MLS: {result.agent.shortId}</span>
              </div>
            ) : (
              <div style={{ fontSize: 13, color: "var(--ep-fg-muted)" }}>
                MLS ID <strong>{result.mlsId}</strong> not found in onboarding database — listings still shown if present in index.
              </div>
            )}
          </div>

          {/* Listings */}
          <CheckSection
            label="Listings"
            count={result.listings.count}
            items={result.listings.items}
            renderRow={(l) => (
              <ListingRow key={l.listingId} l={l} fmtPrice={fmtPrice} />
            )}
          />

          {/* Pendings */}
          <CheckSection
            label="Pending"
            count={result.pendings.count}
            items={result.pendings.items}
            renderRow={(l) => (
              <ListingRow key={l.listingId} l={l} fmtPrice={fmtPrice} />
            )}
          />

          {/* BBAs */}
          <div className="ep-card" style={{ padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: result.bbas.count > 0 ? 12 : 0 }}>
              <div className="ep-eyebrow">BBAs</div>
              <CountBadge n={result.bbas.count} />
              {result.bbas.error && (
                <span style={{ fontSize: 12, color: "var(--ep-fg-muted)" }}>{result.bbas.error}</span>
              )}
            </div>
            {result.bbas.items?.length > 0 && (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--ep-border)" }}>
                    <Th>Deal</Th><Th>Stage</Th><Th>Contact</Th>
                  </tr>
                </thead>
                <tbody>
                  {result.bbas.items.map((b) => (
                    <tr key={b.dealId} style={{ borderBottom: "1px solid var(--ep-border)" }}>
                      <Td>{b.name}</Td><Td>{b.stage}</Td><Td>{b.contact || "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {/* Escrows */}
          <div className="ep-card" style={{ padding: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: result.escrows.count > 0 ? 12 : 0 }}>
              <div className="ep-eyebrow">Escrows</div>
              <CountBadge n={result.escrows.count} />
              {result.escrows.error && (
                <span style={{ fontSize: 12, color: "var(--ep-fg-muted)" }}>{result.escrows.error}</span>
              )}
            </div>
            {result.escrows.items?.length > 0 && (
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--ep-border)" }}>
                    <Th>Address</Th><Th>ID</Th><Th>Represents</Th><Th>Price</Th>
                  </tr>
                </thead>
                <tbody>
                  {result.escrows.items.map((e) => (
                    <tr key={e.txId} style={{ borderBottom: "1px solid var(--ep-border)" }}>
                      <Td>{e.address || "—"}</Td>
                      <Td>{e.customId || e.txId}</Td>
                      <Td>{e.represents || "—"}</Td>
                      <Td>{e.price ? fmtPrice(e.price) : "—"}</Td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </section>
  );
}

function CheckSection({ label, count, items, renderRow }) {
  const [expanded, setExpanded] = useState(true);
  return (
    <div className="ep-card" style={{ padding: 20 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: expanded && count > 0 ? 12 : 0 }}>
        <div className="ep-eyebrow">{label}</div>
        <CountBadge n={count} />
        {count > 0 && (
          <button className="ep-btn ghost sm" onClick={() => setExpanded((v) => !v)} style={{ marginLeft: "auto", fontSize: 12 }}>
            {expanded ? "Collapse" : "Expand"}
          </button>
        )}
      </div>
      {expanded && count > 0 && (
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
          <thead>
            <tr style={{ borderBottom: "1px solid var(--ep-border)" }}>
              <Th>Address</Th><Th>Status</Th><Th>Role</Th><Th>Price</Th>
            </tr>
          </thead>
          <tbody>{items.map(renderRow)}</tbody>
        </table>
      )}
    </div>
  );
}

function ListingRow({ l, fmtPrice }) {
  return (
    <tr style={{ borderBottom: "1px solid var(--ep-border)" }}>
      <Td>{l.address}</Td>
      <Td>{l.status}</Td>
      <Td style={{ color: "var(--ep-fg-muted)" }}>{l.role}</Td>
      <Td>{fmtPrice(l.listPrice) || "—"}</Td>
    </tr>
  );
}

function CountBadge({ n }) {
  if (n === null) return null;
  const cls = n > 0 ? "active" : "draft";
  return <span className={`ep-badge ${cls}`}>{n}</span>;
}

const Th = ({ children }) => (
  <th style={{ textAlign: "left", padding: "6px 8px", fontSize: 11, fontWeight: 600, color: "var(--ep-fg-muted)", textTransform: "uppercase", letterSpacing: "0.06em" }}>{children}</th>
);
const Td = ({ children, style }) => (
  <td style={{ padding: "7px 8px", verticalAlign: "top", ...style }}>{children}</td>
);

function StatusBadge({ feed }) {
  const map = {
    active: { cls: "active", label: "Connected" },
    pending: { cls: "draft", label: "Not connected" },
    error: { cls: "error", label: feed.last_error === "token_expired" ? "Token expired" : "Error" },
  };
  const s = map[feed.status] || { cls: "draft", label: feed.status || "—" };
  return <span className={`ep-badge ${s.cls}`}>{s.label}</span>;
}

function FeedCard({ feed, onChanged }) {
  const [oauthKey, setOauthKey] = useState("");
  const [accessToken, setAccessToken] = useState(""); // write-only; never pre-filled
  const [busy, setBusy] = useState(null); // 'save' | 'test' | 'disconnect'
  const [msg, setMsg] = useState(null);
  const [memberQ, setMemberQ] = useState("");
  const [memberResult, setMemberResult] = useState(null); // null | { found: true, ... } | { found: false }
  const [memberBusy, setMemberBusy] = useState(false);

  function fail(err, fallback) {
    setMsg({ ok: false, text: err instanceof ApiError ? err.message : fallback });
  }

  async function save(e) {
    e.preventDefault();
    setBusy("save"); setMsg(null);
    try {
      // accessToken is write-only: send it only when the admin typed one; blank
      // leaves the stored token untouched (the server treats blank as "keep").
      const body = { oauthKey: oauthKey.trim() };
      if (accessToken.trim()) body.accessToken = accessToken.trim();
      const out = await api.patch(`/admin/mls/feeds/${feed.code}`, body);
      setAccessToken(""); // never retain the token in the field
      setMsg({ ok: true, text: out.status === "active" ? "Saved — feed connected." : "Saved." });
      onChanged();
    } catch (err) { fail(err, "Failed to save."); }
    finally { setBusy(null); }
  }

  async function test() {
    setBusy("test"); setMsg(null);
    try {
      const out = await api.post(`/admin/mls/feeds/${feed.code}/test`, {});
      setMsg({ ok: true, text: `Feed OK (${out.durationMs}ms).` });
    } catch (err) { fail(err, "Test failed."); }
    finally { setBusy(null); }
  }

  async function lookupMember(e) {
    e.preventDefault();
    const q = memberQ.trim().replace(/^tar\./i, "");
    if (!q) return;
    setMemberBusy(true); setMemberResult(null);
    try {
      const out = await api.get(`/admin/mls/feeds/${feed.code}/member?shortId=${encodeURIComponent(q)}`);
      const r = out.results?.[0];
      setMemberResult(r ? { found: true, ...r } : { found: false });
    } catch {
      setMemberResult({ found: false });
    } finally { setMemberBusy(false); }
  }

  async function disconnect() {
    if (!window.confirm(`Disconnect the ${feed.code.toUpperCase()} feed? Its Access Token is cleared and agents lose live data until a new token is pasted.`)) return;
    setBusy("disconnect"); setMsg(null);
    try {
      await api.del(`/admin/mls/feeds/${feed.code}/grant`);
      setMsg({ ok: true, text: "Disconnected." });
      onChanged();
    } catch (err) { fail(err, "Disconnect failed."); }
    finally { setBusy(null); }
  }

  return (
    <div className="ep-card" style={{ padding: 24 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12 }}>
        <div>
          <div style={{ font: "300 22px/1.15 var(--ep-font-display)" }}>{feed.display_name || feed.code.toUpperCase()}</div>
          <div style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginTop: 4 }}>
            Code <code>{feed.code}</code>{feed.mls_id ? <> · MLS {feed.mls_id}</> : null} · {feed.agent_count} agent{feed.agent_count === 1 ? "" : "s"}
          </div>
        </div>
        <StatusBadge feed={feed} />
      </div>

      {feed.last_error && (
        <div style={{ fontSize: 12, color: "var(--ep-rosewood)", marginTop: 10 }}>Last error: {feed.last_error}</div>
      )}

      <div style={{ marginTop: 16, display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12, color: "var(--ep-fg-muted)" }}>
        <span>OAuth Key: <strong>{feed.has_oauth_key ? "set" : "not set"}</strong></span>
        <span>Access Token: <strong>{feed.has_token ? "set" : "not set"}</strong></span>
        {feed.last_sync_at && <span>Last sync: {new Date(feed.last_sync_at).toLocaleString()}</span>}
      </div>

      <form onSubmit={save} style={{ marginTop: 16, display: "grid", gap: 10 }}>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">OAuth Key</span>
          <input className="ep-input" value={oauthKey} onChange={(e) => setOauthKey(e.target.value)} autoComplete="off" placeholder={feed.has_oauth_key ? "•••• (set — paste to replace)" : "App identifier from the MLS"} />
        </label>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Access Token</span>
          <input className="ep-input" type="password" value={accessToken} onChange={(e) => setAccessToken(e.target.value)} autoComplete="new-password" placeholder={feed.has_token ? "•••• (set — leave blank to keep, paste to replace)" : "Long-lived bearer token from the MLS"} />
        </label>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <button type="submit" className="ep-btn primary sm" disabled={busy === "save" || !oauthKey.trim()}>
            {busy === "save" ? "Saving…" : "Save"}
          </button>
          <button type="button" className="ep-btn sm" onClick={test} disabled={busy === "test" || !feed.has_token}>
            {busy === "test" ? "Testing…" : "Test"}
          </button>
          {feed.has_token && (
            <button type="button" className="ep-btn ghost sm" onClick={disconnect} disabled={busy === "disconnect"} style={{ color: "var(--ep-rosewood)" }}>
              {busy === "disconnect" ? "Disconnecting…" : "Disconnect"}
            </button>
          )}
        </div>
      </form>

      {msg && (
        <div style={{ fontSize: 12, marginTop: 12, color: msg.ok ? "var(--ep-saguaro, #2f6b43)" : "var(--ep-rosewood)" }}>{msg.text}</div>
      )}

      {feed.has_token && (
        <div style={{ marginTop: 20, borderTop: "1px solid var(--ep-border)", paddingTop: 16 }}>
          <div className="ep-eyebrow" style={{ marginBottom: 10 }}>Member Lookup</div>
          <form onSubmit={lookupMember} style={{ display: "flex", gap: 8 }}>
            <input
              className="ep-input"
              value={memberQ}
              onChange={(e) => setMemberQ(e.target.value)}
              placeholder="ShortId (e.g. wh062 or 56856)"
              style={{ flex: 1, margin: 0 }}
            />
            <button type="submit" className="ep-btn sm" disabled={memberBusy || !memberQ.trim()}>
              {memberBusy ? "…" : "Look up"}
            </button>
          </form>
          {memberResult && (
            <div style={{ marginTop: 10, fontSize: 12, padding: "10px 12px", background: "var(--ep-bg-elevated)", border: "1px solid var(--ep-border)" }}>
              {memberResult.found ? (
                <table style={{ borderCollapse: "collapse", width: "100%" }}>
                  {[
                    ["Name",    memberResult.Name || [memberResult.FirstName, memberResult.LastName].filter(Boolean).join(" ") || "—"],
                    ["ShortId", memberResult.ShortId || "—"],
                    ["License", memberResult.LicenseNumber || "—"],
                    ["NRDS",    memberResult.NrdsId || "—"],
                    ["Office",  memberResult.Office || "—"],
                    ["Email",   memberResult.Emails?.[0]?.Address || "—"],
                    ["Active",  memberResult.Active ? "Yes" : "No"],
                  ].map(([k, v]) => (
                    <tr key={k}>
                      <td style={{ color: "var(--ep-fg-muted)", paddingRight: 12, whiteSpace: "nowrap" }}>{k}</td>
                      <td style={{ color: "var(--ep-origin)" }}>{v}</td>
                    </tr>
                  ))}
                </table>
              ) : (
                <span style={{ color: "var(--ep-rosewood)" }}>No member found.</span>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ----------------------------------------------------------------------------
// TransferPanel — admin-initiated listing + escrow transfers on behalf of any
// agent. Look up by MLS ID → pick listing or escrow → fill missing fields →
// fire BoldSign without the agent needing to use the wizard.
// ----------------------------------------------------------------------------
const ASSOC_OPTIONS = ["WeSERV","NAAR","PAAR","PAR","SAAR","TAR","WMAR"];

function TransferPanel() {
  const [mlsId, setMlsId]     = useState("");
  const [busy, setBusy]       = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [error, setError]     = useState(null);
  const [result, setResult]   = useState(null); // { agent, listings, escrows }
  const [tab, setTab]         = useState("listings");

  async function lookup(e) {
    e.preventDefault();
    setBusy(true); setError(null); setResult(null);
    try {
      const d = await api.get(`/admin/transfers?mlsId=${encodeURIComponent(mlsId.trim().replace(/^tar\./i, ""))}`);
      setResult(d);
      setTab(d.listings.length > 0 ? "listings" : "escrows");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Lookup failed.");
    } finally { setBusy(false); }
  }

  async function refreshSellers() {
    setSyncing(true); setError(null);
    try {
      await api.post("/admin/transfers/sync-sellers", {});
      // Re-fetch listings so the UI reflects the updated cache.
      if (mlsId.trim()) {
        const d = await api.get(`/admin/transfers?mlsId=${encodeURIComponent(mlsId.trim())}`);
        setResult(d);
      }
    } catch (err) {
      setError("Refresh failed: " + (err instanceof ApiError ? err.message : err.message));
    } finally { setSyncing(false); }
  }

  return (
    <section>
      <div className="ep-eyebrow" style={{ color: "var(--ep-sunset)" }}>Transfer Support</div>
      <h2 style={{ font: "300 24px/1.15 var(--ep-font-display)", margin: "6px 0 4px" }}>Admin transfers</h2>
      <p style={{ fontSize: 13, color: "var(--ep-fg-muted)", maxWidth: "62ch", marginBottom: 20 }}>
        Look up an agent by MLS ID to initiate listing or escrow transfers on their behalf.
      </p>

      <form onSubmit={lookup} style={{ display: "flex", gap: 8, maxWidth: 400, marginBottom: 24 }}>
        <input className="ep-input" value={mlsId} onChange={(e) => setMlsId(e.target.value)}
          placeholder="Short MLS ID, e.g. wh062" autoComplete="off" style={{ flex: 1 }} />
        <button type="submit" className="ep-btn primary sm" disabled={busy || !mlsId.trim()}>
          {busy ? "Looking up…" : "Look up"}
        </button>
      </form>

      {error && <div style={{ fontSize: 13, color: "var(--ep-rosewood)", marginBottom: 16 }}>{error}</div>}

      {result && (
        <div style={{ maxWidth: 820 }}>
          {/* Agent card */}
          <div className="ep-card" style={{ padding: "14px 20px", marginBottom: 20, display: "flex", alignItems: "center", gap: 16 }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", background: "var(--ep-sunset-tint, rgba(180,80,30,0.12))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 600, color: "var(--ep-sunset)", flexShrink: 0 }}>
              {result.agent.name.split(" ").map(w => w[0]).slice(0,2).join("")}
            </div>
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 15 }}>{result.agent.name}</strong>
              <span style={{ color: "var(--ep-fg-muted)", fontSize: 13, marginLeft: 12 }}>{result.agent.email}</span>
              <span style={{ color: "var(--ep-fg-muted)", fontSize: 13, marginLeft: 12 }}>MLS: {result.agent.mlsId}</span>
            </div>
            <span className={`ep-badge ${result.agent.hasBoldTrail ? "active" : "draft"}`}>
              {result.agent.hasBoldTrail ? "BoldTrail linked" : "No BoldTrail link"}
            </span>
            <button className="ep-btn ghost sm" onClick={refreshSellers} disabled={syncing} title="Re-sync seller contacts from BoldTrail">
              {syncing ? "Refreshing…" : "↻ Refresh BoldTrail"}
            </button>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", borderBottom: "1px solid var(--ep-border)", marginBottom: 20 }}>
            {[["listings", `Listings (${result.listings.length})`], ["escrows", `Escrows (${result.escrows.length})`]].map(([k, label]) => (
              <button key={k} onClick={() => setTab(k)} style={{ padding: "8px 18px", fontSize: 13, background: "none", border: "none", borderBottom: tab === k ? "2px solid var(--ep-sunset)" : "2px solid transparent", cursor: "pointer", fontWeight: tab === k ? 600 : 400, color: tab === k ? "var(--ep-fg)" : "var(--ep-fg-muted)", fontFamily: "var(--ep-font-body)" }}>
                {label}
              </button>
            ))}
          </div>

          {tab === "listings" && (
            <ListingTransferList listings={result.listings} agentId={result.agent.id} agentName={result.agent.name} />
          )}
          {tab === "escrows" && (
            <EscrowTransferList escrows={result.escrows} agentId={result.agent.id} agentName={result.agent.name} hasBoldTrail={result.agent.hasBoldTrail} />
          )}
        </div>
      )}
    </section>
  );
}

function ListingTransferList({ listings, agentId, agentName }) {
  const [selected, setSelected] = useState(null);

  if (!listings.length) return <p style={{ fontSize: 13, color: "var(--ep-fg-muted)" }}>No active listings found in replication index.</p>;

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {listings.map((l) => (
        <div key={l.listingId}>
          <div
            onClick={() => setSelected(selected?.listingId === l.listingId ? null : l)}
            style={{ background: "var(--ep-bg-elevated)", border: `1px solid ${selected?.listingId === l.listingId ? "var(--ep-sunset)" : "var(--ep-border)"}`, borderRadius: 4, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
          >
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 14 }}>{l.addr}</strong>
              <span style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginLeft: 10 }}>{l.city}</span>
            </div>
            <div style={{ fontSize: 13, color: "var(--ep-fg-muted)", marginRight: 8 }}>{l.status}</div>
            {l.listPrice && <div style={{ fontSize: 13, fontWeight: 600 }}>${Number(l.listPrice).toLocaleString()}</div>}
            <span className={`ep-badge ${l.sellerSource === "boldtrail" ? "active" : "draft"}`}>{l.sellerSource === "boldtrail" ? "sellers found" : "no sellers"}</span>
            <span className={`ep-badge ${l.role === "list" ? "signed" : "draft"}`}>{l.role}</span>
          </div>
          {selected?.listingId === l.listingId && (
            <ListingTransferForm listing={l} agentId={agentId} agentName={agentName} onClose={() => setSelected(null)} />
          )}
        </div>
      ))}
    </div>
  );
}

function ListingTransferForm({ listing, agentId, agentName, onClose }) {
  const [assoc, setAssoc]     = useState(listing.association && ASSOC_OPTIONS.includes(listing.association) ? listing.association : "WeSERV");
  const [seller1, setSeller1] = useState({ name: listing.seller?.name || "", email: listing.seller?.email || "" });
  const hasBTSeller2 = !!(listing.seller2?.name || listing.seller2?.email);
  const [includeSeller2, setIncludeSeller2] = useState(hasBTSeller2);
  const [seller2, setSeller2] = useState({ name: listing.seller2?.name || "", email: listing.seller2?.email || "" });
  const [preview, setPreview] = useState(false);
  const [busy, setBusy]       = useState(false);
  const [result, setResult]   = useState(null);
  const [error, setError]     = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null); setResult(null);
    try {
      const body = {
        agentId,
        mlsId:       listing.listingId,
        association: assoc,
        preview,
        listing: {
          ...listing,
          seller:  seller1.email ? seller1 : undefined,
          seller2: (includeSeller2 && seller2.email) ? seller2 : undefined,
        },
      };
      const d = await api.post("/admin/transfers/listing", body);
      setResult(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Transfer failed.");
    } finally { setBusy(false); }
  }

  if (result) return (
    <div className="ep-card" style={{ margin: "8px 0 0", padding: 20 }}>
      <div style={{ fontSize: 13, color: "var(--ep-saguaro, #2f6b43)", marginBottom: 8, fontWeight: 600 }}>
        Sent to BoldSign — envelope {result.envelopeId}
      </div>
      {result.dashboardUrl && <a href={result.dashboardUrl} target="_blank" rel="noreferrer" style={{ fontSize: 12 }}>View in BoldSign ↗</a>}
      <div style={{ marginTop: 12 }}>
        <button className="ep-btn ghost sm" onClick={onClose}>Close</button>
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} className="ep-card" style={{ margin: "8px 0 0", padding: 20 }}>
      <div className="ep-eyebrow" style={{ marginBottom: 14 }}>Listing transfer — {agentName}</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Association</span>
          <select className="ep-input" value={assoc} onChange={(e) => setAssoc(e.target.value)}>
            {ASSOC_OPTIONS.map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        </label>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">MLS #</span>
          <input className="ep-input" value={listing.listingId} readOnly style={{ opacity: 0.7 }} />
        </label>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 14 }}>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Seller 1 name</span>
          <input className="ep-input" value={seller1.name} onChange={(e) => setSeller1(s => ({ ...s, name: e.target.value }))} placeholder="Full name" />
        </label>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Seller 1 email</span>
          <input className="ep-input" type="email" value={seller1.email} onChange={(e) => setSeller1(s => ({ ...s, email: e.target.value }))} placeholder="seller@example.com" />
        </label>
      </div>

      <div style={{ marginBottom: 16 }}>
        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", color: "var(--ep-fg-muted)", marginBottom: includeSeller2 ? 10 : 0 }}>
          <input type="checkbox" checked={includeSeller2} onChange={(e) => setIncludeSeller2(e.target.checked)} style={{ accentColor: "var(--ep-sunset)" }} />
          Include Seller 2
          {hasBTSeller2 && <span style={{ fontSize: 11, color: "var(--ep-saguaro, #2f6b43)" }}>· from BoldTrail</span>}
        </label>
        {includeSeller2 && (
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <label className="ep-field" style={{ margin: 0 }}>
              <span className="ep-eyebrow">Seller 2 name</span>
              <input className="ep-input" value={seller2.name} onChange={(e) => setSeller2(s => ({ ...s, name: e.target.value }))} placeholder="Full name" />
            </label>
            <label className="ep-field" style={{ margin: 0 }}>
              <span className="ep-eyebrow">Seller 2 email</span>
              <input className="ep-input" type="email" value={seller2.email} onChange={(e) => setSeller2(s => ({ ...s, email: e.target.value }))} placeholder="seller2@example.com" />
            </label>
          </div>
        )}
      </div>

      <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: 16, color: "var(--ep-fg-muted)" }}>
        <input type="checkbox" checked={preview} onChange={(e) => setPreview(e.target.checked)} style={{ accentColor: "var(--ep-sunset)" }} />
        Preview mode — route all signers to onboard@everyplace.com
      </label>

      {error && <div style={{ fontSize: 13, color: "var(--ep-rosewood)", marginBottom: 12 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="ep-btn primary sm" disabled={busy || !seller1.email}>
          {busy ? "Sending…" : preview ? "Send preview" : "Send to BoldSign"}
        </button>
        <button type="button" className="ep-btn ghost sm" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}

function EscrowTransferList({ escrows, agentId, agentName, hasBoldTrail }) {
  const [selected, setSelected] = useState(null);

  if (!hasBoldTrail) return (
    <div style={{ fontSize: 13, color: "var(--ep-fg-muted)", padding: "12px 0" }}>
      This agent has no BoldTrail user ID linked — escrows cannot be fetched until their account is matched.
    </div>
  );
  if (!escrows.length) return <p style={{ fontSize: 13, color: "var(--ep-fg-muted)" }}>No pending transactions found in BoldTrail.</p>;

  return (
    <div style={{ display: "grid", gap: 8 }}>
      {escrows.map((e) => (
        <div key={e.bmId}>
          <div
            onClick={() => setSelected(selected?.bmId === e.bmId ? null : e)}
            style={{ background: "var(--ep-bg-elevated)", border: `1px solid ${selected?.bmId === e.bmId ? "var(--ep-sunset)" : "var(--ep-border)"}`, borderRadius: 4, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}
          >
            <div style={{ flex: 1 }}>
              <strong style={{ fontSize: 14 }}>{e.address}</strong>
              <span style={{ fontSize: 12, color: "var(--ep-fg-muted)", marginLeft: 10 }}>{e.city}</span>
            </div>
            {e.escrowNumber && <span style={{ fontSize: 12, color: "var(--ep-fg-muted)" }}>#{e.escrowNumber}</span>}
            <span style={{ fontSize: 13, fontWeight: 600 }}>{e.price}</span>
            <span className="ep-badge draft">{e.represents}</span>
          </div>
          {selected?.bmId === e.bmId && (
            <EscrowTransferForm escrow={e} agentId={agentId} agentName={agentName} onClose={() => setSelected(null)} />
          )}
        </div>
      ))}
    </div>
  );
}

function EscrowTransferForm({ escrow, agentId, agentName, onClose }) {
  const defaultRepresents = escrow.represents?.includes("buyer") ? "buyer" : "seller";
  const [represents, setRepresents] = useState(defaultRepresents);
  const [escrowNumber, setEscrowNumber] = useState(escrow.escrowNumber || "");
  const [transferFee, setTransferFee]   = useState("0");
  const [buyer,  setBuyer]  = useState({ name: escrow.buyer?.name  || "", email: escrow.buyer?.email  || "", phone: escrow.buyer?.phone  || "" });
  const [seller, setSeller] = useState({ name: escrow.seller?.name || "", email: escrow.seller?.email || "", phone: escrow.seller?.phone || "" });
  const [buyer2,  setBuyer2]  = useState({ name: escrow.buyer2?.name  || "", email: escrow.buyer2?.email  || "", phone: escrow.buyer2?.phone  || "" });
  const [seller2, setSeller2] = useState({ name: escrow.seller2?.name || "", email: escrow.seller2?.email || "", phone: escrow.seller2?.phone || "" });
  const [busy, setBusy]     = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError]   = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null); setResult(null);
    try {
      const d = await api.post(`/admin/transfers/escrow/${encodeURIComponent(escrow.bmId)}`, {
        agentId,
        represents,
        escrowNumber,
        transferFee: Number(transferFee) || 0,
        escrowCompany: escrow.escrowCompany || "",
        buyer,
        seller,
        buyer2:  (represents === "buyer"  && buyer2.name?.trim()  && buyer2.email?.trim())  ? buyer2  : undefined,
        seller2: (represents === "seller" && seller2.name?.trim() && seller2.email?.trim()) ? seller2 : undefined,
      });
      setResult(d);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Transfer failed.");
    } finally { setBusy(false); }
  }

  const party = represents === "buyer" ? buyer : seller;
  const setParty = represents === "buyer" ? setBuyer : setSeller;
  const party2 = represents === "buyer" ? buyer2 : seller2;
  const setParty2 = represents === "buyer" ? setBuyer2 : setSeller2;

  if (result) return (
    <div className="ep-card" style={{ margin: "8px 0 0", padding: 20 }}>
      <div style={{ fontSize: 13, color: "var(--ep-saguaro, #2f6b43)", marginBottom: 8, fontWeight: 600 }}>
        Sent to BoldSign — envelope {result.envelopeId}
      </div>
      <div style={{ marginTop: 12 }}>
        <button className="ep-btn ghost sm" onClick={onClose}>Close</button>
      </div>
    </div>
  );

  return (
    <form onSubmit={submit} className="ep-card" style={{ margin: "8px 0 0", padding: 20 }}>
      <div className="ep-eyebrow" style={{ marginBottom: 14 }}>Escrow transfer — {agentName}</div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 14 }}>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Represents</span>
          <select className="ep-input" value={represents} onChange={(e) => setRepresents(e.target.value)}>
            <option value="buyer">Buyer</option>
            <option value="seller">Seller</option>
          </select>
        </label>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Escrow number</span>
          <input className="ep-input" value={escrowNumber} onChange={(e) => setEscrowNumber(e.target.value)} placeholder="ESC-12345" />
        </label>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Transfer fee ($)</span>
          <input className="ep-input" type="number" min="0" step="0.01" value={transferFee} onChange={(e) => setTransferFee(e.target.value)} />
        </label>
      </div>

      <div className="ep-eyebrow" style={{ marginBottom: 8 }}>{represents === "buyer" ? "Buyer" : "Seller"} details</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Name</span>
          <input className="ep-input" value={party.name} onChange={(e) => setParty(p => ({ ...p, name: e.target.value }))} placeholder="Full name" />
        </label>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Email</span>
          <input className="ep-input" type="email" value={party.email} onChange={(e) => setParty(p => ({ ...p, email: e.target.value }))} placeholder="party@example.com" />
        </label>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Phone</span>
          <input className="ep-input" value={party.phone} onChange={(e) => setParty(p => ({ ...p, phone: e.target.value }))} placeholder="(555) 000-0000" />
        </label>
      </div>

      <div className="ep-eyebrow" style={{ marginBottom: 8 }}>
        {represents === "buyer" ? "Buyer 2" : "Seller 2"}
        <span style={{ fontSize: 11, fontWeight: 400, color: "var(--ep-fg-muted)", marginLeft: 6 }}>(optional — both name and email required to include)</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 16 }}>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Name</span>
          <input className="ep-input" value={party2.name} onChange={(e) => setParty2(p => ({ ...p, name: e.target.value }))} placeholder="Full name" />
        </label>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Email</span>
          <input className="ep-input" type="email" value={party2.email} onChange={(e) => setParty2(p => ({ ...p, email: e.target.value }))} placeholder="party2@example.com" />
        </label>
        <label className="ep-field" style={{ margin: 0 }}>
          <span className="ep-eyebrow">Phone</span>
          <input className="ep-input" value={party2.phone} onChange={(e) => setParty2(p => ({ ...p, phone: e.target.value }))} />
        </label>
      </div>

      {error && <div style={{ fontSize: 13, color: "var(--ep-rosewood)", marginBottom: 12 }}>{error}</div>}

      <div style={{ display: "flex", gap: 8 }}>
        <button type="submit" className="ep-btn primary sm" disabled={busy || !party.email || !escrowNumber}>
          {busy ? "Sending…" : "Send to BoldSign"}
        </button>
        <button type="button" className="ep-btn ghost sm" onClick={onClose}>Cancel</button>
      </div>
    </form>
  );
}
